import { Endgame, LevelSession, Modes, SuspendedLevel, checkpointFrom, fixedRunSeed, retryCheckpoint, runProfileFor } from "@highmarch/app";
import { GearItem, LevelModifiers, LevelPhase, isPlainModifiers } from "@highmarch/domain";
import { reductionLevel, runOwned, runPower, todaysRoad } from "./fairRun";
import { systemClock } from "../infra/adapters";
import { audio } from "../audio/audio";
import { GroundMesh } from "../render/groundMesh";
import { FoliageView } from "../render/views/foliage";
import { DetailScatter } from "../render/views/detail";
import { BaseSkin } from "../render/views/baseSkin";
import { levelAssetInput, levelAssetNeeds } from "../render/assetNeeds";
import { LevelController } from "../level/levelController";
import { LevelUiScope, clearHubLeftovers } from "../level/levelUiScope";
import { LevelEndChoice } from "../ui/screens";
import { showSettings } from "../ui/settings";
import { cloudWipe } from "../ui/transition";
import { LevelLoading, LoadRunKind, runLevelLoad } from "../ui/levelLoading";
import { biomeOf, type Biome } from "../ui/chapters";
import { HUB_UNLOCK_LEVEL } from "../ui/unlockCards";
import { guide } from "../ui/guide/guide";
import { mountRunTag } from "../ui/runTag";
import type { GameServices, Nav, Screens } from "./context";
import type { HubFlow } from "./hubFlow";
import type { MetaFlow } from "./metaFlow";
import { features } from "../features/registry";
import { suspendedMode } from "../keeper";
import { RunBank, levelRunBank } from "./runBank";

/** Every chest model, preloaded in the background during a level (see startLevel). */
const CHEST_MODELS = ["common", "uncommon", "rare", "epic", "legendary", "mythic"].map((r) => `chest_${r}`);
/** How long after a level is built the chest models start loading (its own first frames come first). */
const CHEST_PRELOAD_DELAY_MS = 1500;

/**
 * Part of the composition root: a level from PLAY (or CONTINUE, RETRY, NEXT) to its end — the load under the clouds,
 * the build, the chest banked once, the result recorded — and back to the hub or on to the next level.
 */
export class LevelFlow {
  /** Only the latest start builds (see startLevel). */
  private levelRequest = 0;
  /** The current level's result and chest, banked once as the run ends (boot/runBank.ts), before the end screen draws. */
  private bank: RunBank | null = null;

  constructor(private readonly g: GameServices, private readonly screens: Screens, private readonly meta: MetaFlow, private readonly hub: HubFlow, private readonly nav: Nav) {
    // ---- WP6 hook: Heroic and Weekly Trial runs start here from the Events sheet and the campaign map. ----
    features.startRun = (level, modifiers) => this.requestStart(level, modifiers);
  }

  /**
   * PLAY or a level on the map: a suspended battle is continued (same level) or has to be abandoned first. The clouds
   * hold until the level is built (or the player backs out of a slow load); taps meanwhile are ignored (`starts`).
   */
  requestStart(level: number, modifiers: LevelModifiers | null = null): void {
    const s = this.g.keeper.suspended;
    // M21: an Iron or Endless run put aside continues only from its own entry (Endless borrows a campaign level's ground).
    const mode = modifiers?.endless ? "endless" : modifiers?.iron ? "iron" : modifiers?.daily ? "daily" : modifiers?.challenge ? "challenge" : modifiers?.event?.mode ?? null;
    if (s && s.record.level === level && suspendedMode(s) === mode) { this.continueSuspended(); return; }
    this.g.starts.run(() => this.g.keeper.confirmAbandon(level).then((go) => (go ? cloudWipe(() => this.startLevel(level, null, modifiers), { hold: true }) : undefined)));
  }

  /** CONTINUE: rebuilds the suspended battle under the clouds (startLevel replays it) and opens it paused. */
  continueSuspended(): void {
    const s = this.g.keeper.suspended;
    if (s) this.g.starts.run(() => cloudWipe(() => this.startLevel(s.record.level, s), { hold: true }));
  }

  /**
   * `resume`: rebuild that suspended battle (replayed under the wipe) instead of starting a fresh attempt. Runs under
   * clouds that hold until it settles (transition.ts `hold`), with the load's progress on them (levelLoading.ts); it
   * settles with the level built, or — CANCEL, or BACK after files failed — with the screen it was started from.
   */
  async startLevel(level: number, resume: SuspendedLevel | null = null, modifiers: LevelModifiers | null = null): Promise<void> {
    // The chapter's biome picks the ground's tiling map set (meadow / marsh / ashlands). Loading is cached per
    // biome, so this is a no-op after the first level in a chapter; on a cold load it resolves before the level
    // builds. The level's own HQ set (render/assetNeeds.ts) loads alongside it, while the wipe covers the screen.
    // Only the latest request builds: a slow first load of one biome (or its HQ set) must not land after a later,
    // cached start.
    const request = ++this.levelRequest;
    const biome = biomeOf(level);
    const lib = this.screens.lib;
    // M37: an Endless run's rare is its *through*'s chapter's (domain sim/rares.ts rareLevelOf)
    const rareLevel = (resume ? resume.record.modifiers : modifiers)?.endless?.through ?? level;
    const owned = runOwned(this.g.catalog, resume, modifiers);   // MP-32: a fair run loads the normalised towers' models
    const needsFor = (groundMesh: boolean) => lib && !this.g.keepAllHq
      ? levelAssetNeeds({ ...levelAssetInput(this.g.balance, level, owned, biome, BaseSkin.current(), groundMesh, rareLevel), ...(resume ? { pet: resume.record.modifiers?.pet?.id ?? null } : {}) }, lib.shippedNames)
      : null;
    // A new biome decodes ~110 MiB of terrain maps. Loading it with the old screen still up held both biomes' maps
    // and both screens' models at once (hub → ashlands peaked ~220 MiB of terrain), so the old screen goes first —
    // it is under the cloud wipe — and takes its maps and the models the level will not use with it. Within a biome
    // nothing new decodes, and the old screen stays until the level replaces it (buildLevel), as before.
    const cleared = !GroundMesh.isCached(biome);
    if (cleared) this.clearForLoad(needsFor(true), biome);
    // Progress for the panel: the level's models in memory (whoever fetched them: the hub's preload counts), plus
    // one for the ground.
    let wanted = needsFor(true), groundIn = false;
    const measure = () => {
      const names = lib && wanted ? [...wanted].filter((n) => lib.shippedNames.has(n)) : [];
      return { done: names.filter((n) => lib!.has(`hq:${n}`)).length + (groundIn ? 1 : 0), total: names.length + 1 };
    };
    const load = async (): Promise<string[]> => {
      // the models that do not depend on the ground start with it (ensure joins any the preload has in flight)
      const first = wanted;
      const early = first ? lib!.ensure(first) : Promise.resolve([]);
      await GroundMesh.load(biome);
      groundIn = true;
      wanted = needsFor(GroundMesh.available);
      // only what the ground added (the grass tiles when its maps did not ship): a model that just failed is not retried
      // until the player asks (RETRY)
      const rest = wanted ? [...wanted].filter((n) => !first?.has(n)) : [];
      const failed = [...await early, ...(rest.length > 0 ? await lib!.ensure(rest) : [])];
      return [...new Set(failed)].filter((n) => !lib!.has(`hq:${n}`));
    };
    // the war map's heading in the run's own words (M21: Iron and Endless runs; a battle put aside picks up again)
    const kind: LoadRunKind = resume ? "resume" : modifiers?.endless ? "endless" : modifiers?.iron ? "iron" : modifiers?.daily ? "daily" : modifiers?.challenge ? "challenge" : "campaign";
    const panel = new LevelLoading(measure, { balance: this.g.balance, level, kind, owned });
    let outcome;
    try {
      outcome = await runLevelLoad(load, panel);
      if (outcome === "ready") await panel.finish();            // the road reaches the gate before the clouds part
    } finally { panel.dispose(); }
    if (request !== this.levelRequest) return;
    if (outcome === "back") { this.levelRequest++; return this.backFromLoad(cleared); }
    const needs = needsFor(GroundMesh.available);
    const resumed = resume ? await this.g.keeper.replay(resume) : null;
    if (request !== this.levelRequest) return;
    if (resume && !resumed) return this.nav.showMenu();
    this.buildLevel(level, needs, resumed, modifiers);
    // The end-of-level chest is the one model the level set cannot name (its rarity is rolled at the end), and
    // loading it on demand left the end card showing an empty glow while it streamed in. Fetch the six chests
    // (~4.6 MB) in the background once the level is up; the next screen's set frees them again.
    if (needs) setTimeout(() => { void this.screens.lib?.ensure(CHEST_MODELS); }, CHEST_PRELOAD_DELAY_MS);
  }

  /**
   * The player backed out of a level load: the screen it was started from comes back. The hub or the map is still
   * up for a start within its biome; after a new biome's load took it down (clearForLoad), or a start from a level's
   * end screen, the hub is built again in the biome it stood in (its ground maps load again if they were freed).
   */
  private async backFromLoad(cleared: boolean): Promise<void> {
    await GroundMesh.load(FoliageView.biome);                 // the maps the screen was drawn with, not the level's
    if (!cleared && (this.screens.hub || this.screens.campaign)) return;
    return this.nav.showMenu();
  }

  /** Takes the current screen down and frees what the next level will not use, before that level's biome loads. */
  private clearForLoad(needs: ReadonlySet<string> | null, biome: Biome): void {
    this.hub.closeScreens();
    this.screens.level?.dispose();
    this.screens.level = null;
    GroundMesh.drop();
    if (needs) this.hub.releaseAssets(needs, biome);
    else GroundMesh.release(biome);
  }

  private buildLevel(level: number, needs: ReadonlySet<string> | null = null, resumed: LevelSession | null = null, modifiers: LevelModifiers | null = null, rebuilt = false): void {
    const { catalog, keeper, save } = this.g, s = this.screens;
    // the prop and detail pools swap with the ground's maps, or the ashlands grow meadow oaks out of the cinders
    FoliageView.biome = DetailScatter.biome = biomeOf(level);
    audio.music("battle");
    this.hub.closeScreens();
    s.level?.dispose();
    s.level = null;
    // An open hub sheet, its scrim or the achievement banner would otherwise stay up under the battle's HUD (M32).
    clearHubLeftovers(this.g.ui);
    if (needs) this.hub.releaseAssets(needs, biomeOf(level));
    this.bank = null;
    // A resumed battle keeps its own attempt, seed and inputs (the session carries them); a fresh start drops any
    // suspended one (requestStart asked first).
    if (!resumed) keeper.clear();
    // MP-20: a Daily Road run's seed is the day's (the same for everyone), no attempt of the level it borrows
    const seed = resumed ? resumed.seed : fixedRunSeed(modifiers) ?? this.g.seeds.levelSeed(save.playerId, level, catalog.beginAttempt(level));
    // ---- MP-06/MP-25 hook: a new attempt takes the armed supplies and a farm twist into its modifiers (and out of the stock). ----
    if (!resumed) { modifiers = features.prepareRun(level, modifiers); this.meta.persist(); }
    // ---- M21 hook: an Iron, Endless or Daily Road run (docs/MODES.md) is never a first clear, and a fresh Iron run spends the day's try. ----
    const runMods = resumed?.modifiers ?? modifiers;
    const modeRun = runMods?.iron !== undefined || runMods?.endless !== undefined || runMods?.daily !== undefined || runMods?.event !== undefined || runMods?.challenge !== undefined;
    if (!resumed && modifiers?.iron) { new Modes(catalog).beginIron(level, systemClock.utcNowSeconds()); this.meta.persist(); }
    if (!resumed && modifiers) features.runBegan(level, modifiers);   // MP-21: an Odyssey map begins its attempt
    const firstClear = resumed ? resumed.firstClear : !modeRun && !catalog.isCleared(level);
    // ---- WP3a hook: run modifiers (Heroic, Weekly Trial; DESIGN §5.1). A fresh run with modifiers builds its session
    // here and hands it over prebuilt (the resume slot: for a session still in its opening draft that only draws it).
    // The modifiers travel in the session's record, so suspend/CONTINUE and a checkpoint retry keep them. ----
    // ---- MP-32 hook: a fair mode's run takes the normalised profile, never the save. A fresh run also takes the battle
    // boosts running now (owner 2026-10-21, app boosts.ts) into its gear; a resumed one keeps the gear it was built with. ----
    const profile = runProfileFor(catalog, modifiers, resumed ? null : systemClock.utcNowSeconds());
    const session = resumed ?? (modifiers ? new LevelSession(this.g.balance, level, seed, profile.owned, profile.towerLevels, firstClear, profile.gear, modifiers) : null);
    // A build that throws part-way must not leave the HUD it had put up (M32): its nodes go, and the hub comes back.
    const scope = new LevelUiScope(this.g.ui);
    let lv: LevelController;
    try {
      lv = new LevelController(this.g.rig, this.g.ui, this.g.balance, level, seed, catalog.ownedTowers(), catalog.towerLevels(), firstClear, (l, won, choice) => this.onLevelEnded(l, won, choice), profile.gear, () => this.openCurrentChest(),
        runPower(catalog, runMods), session, rebuilt);
    } catch (err) {
      console.error("[level] build failed", err);
      scope.claim();
      scope.release();
      void this.nav.showMenu();
      return;
    }
    s.level = lv;
    // ---- WP9 hook: the Heroic / Weekly Trial tag on the HUD (the gap WP6 left; a resumed or undone run keeps it). ----
    mountRunTag(this.g.ui, new Modes(catalog).runKind(lv.session.modifiers) ?? new Endgame(catalog).runKind(level, lv.session.modifiers));
    this.meta.watchBattle(lv.sim);
    lv.rareGear = () => this.meta.rareGearNow(lv.sim);       // M36: the rare chest reveal shows what was banked
    lv.wyrmHooks = this.meta.wyrm.hooks(lv.sim);             // M40: the Starfall Wyrm's reward, retry and skin
    lv.onCheckpoint = () => keeper.suspend(s.level);
    // M21: an Iron or Endless run keeps no campaign best (its own bests are the modes section's) and has no defeat lesson.
    const bank = this.bank = levelRunBank(catalog, level, lv.session.modifiers, () => this.screens.level?.sim.chest ?? null, () => this.meta.persist());
    // M33: the result and the chest are in the save before the end screen (and its 3D chest) is drawn
    lv.onRunOver = (reached, won) => { bank.settle(won); if (modeRun) { keeper.clear(); return null; } return keeper.runOver(level, reached, won); };
    lv.onSettings = () => showSettings(this.g.ui, { resetSave: () => this.meta.resetSave(), playerId: save.playerId, quitLevel: () => s.level?.quit() });
    lv.onAwake = (on) => this.g.wakeLock.hold(on);
    lv.onUndo = (session) => this.undoCard(level, session);
    lv.onStars = (run) => { if (lv.session.modifiers.daily || lv.session.modifiers.event || lv.session.modifiers.challenge) return 0; const { before } = catalog.recordStars(level, run); this.meta.persist(); return before; };   // MP-20: none on a borrowed layout
    this.g.wakeLock.hold(!lv.paused);
  }

  /**
   * UNDO before the waves (WP2, D17): the level is rebuilt around the replayed session with the view kept, and the
   * suspended copy follows it (a log that only grows is the anti-save-scum rule once the waves run, not in the draft).
   */
  private undoCard(level: number, session: LevelSession): void {
    this.buildLevel(level, null, session, null, true);
    this.g.keeper.clear();
    this.g.keeper.suspend(this.screens.level);
  }

  /** OPEN on the end screen: the chest is already banked (settled as the run ended); this hands over its gear. */
  private openCurrentChest(): GearItem[] { return this.bank?.takeGear() ?? []; }

  /** The level goes, whatever its teardown throws (LevelController.dispose logs each part), and the game lets the screen sleep. */
  private disposeLevel(): void {
    const lv = this.screens.level;
    this.screens.level = null;
    // The bank's closures share buildLevel's scope, which holds the LevelController: kept, they kept the whole last
    // level (its scene graph, views, sim) alive under the hub until the next level was built.
    this.bank = null;
    try { lv?.dispose(); } finally { this.g.wakeLock.hold(false); }
  }

  private onLevelEnded(level: number, won: boolean, choice: LevelEndChoice): void {
    // held: RETRY and NEXT load a level under these clouds (with its progress panel and CANCEL)
    void cloudWipe(() => this.leaveLevel(level, won, choice), { hold: true }).then(() => this.g.keeper.announceJobs());
  }

  /** What leaving a finished (or quit) level books: the day's orders, the result and chest, finished upgrades. */
  private settleLeaving(level: number, won: boolean): void {
    const { catalog, keeper } = this.g;
    this.meta.recordBattleOrders();
    this.bank?.settle(won);                                      // a no-op when the run's end already banked it (M33)
    const now = systemClock.utcNowSeconds();
    catalog.profile.applyPlayReduction(reductionLevel(this.screens.level?.sim, level, catalog), won, now);
    keeper.leftLevel(won, catalog.profile.claimCompleted(now));
    this.meta.persist();
  }

  /**
   * The graphics are being rebuilt in place (main.ts `rebuildGraphics`, M33): a battle still under way is saved as its
   * suspended copy and taken down — returns its level, to be continued from that copy; a finished one (the end screen)
   * is left the ordinary way, its result banked. Returns null when no battle is to be continued.
   */
  takeDownForRebuild(): number | null {
    const lv = this.screens.level;
    if (!lv) return null;
    const level = lv.sim.levelNumber;
    if (!lv.isFinished) {
      this.g.keeper.suspend(lv);
      this.disposeLevel();
      return this.g.keeper.suspended?.record.level === level ? level : null;
    }
    try { this.settleLeaving(level, lv.sim.phase === LevelPhase.Won); } catch (err) { console.error("[level] rebuild: bookkeeping failed", err); }
    this.disposeLevel();
    return null;
  }

  private leaveLevel(level: number, won: boolean, choice: LevelEndChoice): Promise<void> | void {
    const { catalog } = this.g;
    // ---- WP3a hook: the checkpoint retry (DESIGN §4.8.8), cut from the lost run before its level goes. ----
    const lost = this.screens.level?.session ?? null;
    const cp = choice === "checkpoint" && lost ? retryCheckpoint(lost) : null;
    const retry = cp && lost ? checkpointFrom(lost, cp, systemClock.utcNowSeconds()) : null;
    // M21: an Endless run always ends lost; it is not a result of the level whose ground it borrows.
    const mode = lost ? new Modes(catalog).runKind(lost.modifiers) : null;
    try {
      this.settleLeaving(level, won);
    } catch (err) {
      // the bookkeeping failed (logged): the level still goes, or the player is left on a dead battle (M32)
      console.error("[level] leaving: bookkeeping failed", err);
    } finally {
      this.disposeLevel();
    }
    // M21: Iron has one try a day (no RETRY, no NEXT); Endless PLAY AGAIN starts a fresh run at today's progress.
    if (mode === "iron" && (choice === "retry" || choice === "next")) return this.nav.showMenu();
    if (mode === "endless") return choice === "retry" ? this.startLevel(level, null, new Modes(catalog).endlessRun()) : this.nav.showMenu();
    if (mode === "challenge") return choice === "retry" && lost ? this.startLevel(level, null, lost.modifiers) : this.nav.showMenu();   // MP-28: the same code again
    if (mode === "daily") { const road = todaysRoad(this.g.balance, systemClock.utcNowSeconds()); return choice === "retry" ? this.startLevel(road.level, null, road.modifiers) : this.nav.showMenu(); }
    // MP-21: an event mode's next run (Boss Week's next tier or the same again, Race Week's road, the Odyssey's next map or attempt)
    if (lost?.modifiers.event) { const n = choice === "retry" || choice === "next" ? features.nextEventRun(lost.modifiers, won) : null; return n ? this.startLevel(n.level, null, n.modifiers) : this.nav.showMenu(); }
    // WP6: RETRY replays a Heroic or Trial run with its modifiers (a plain run has none to keep).
    if (choice === "retry") return this.startLevel(level, null, lost && !isPlainModifiers(lost.modifiers) ? lost.modifiers : null);
    if (retry) return this.startLevel(level, retry);             // same attempt and seed, rebuilt by replay under the clouds
    // The first win goes back to the hub while the captain's first session (equip → upgrade → PLAY level 2) has steps left.
    if (choice === "next" && won && catalog.highestLevelCleared === HUB_UNLOCK_LEVEL && !(guide()?.isDone("hub.play2") ?? true)) return this.nav.showMenu();
    if (choice === "next" && catalog.isUnlocked(level + 1)) return this.startLevel(level + 1);
    return this.nav.showMenu();
  }
}
